<style scoped>
.user-list-item {
    display: flex;
    align-items: center;
    padding: 0.2rem;
}
</style>

<template>
    <v-dialog v-model="dialog" max-width="400px" scrollable content-class="z-full-width-dialog">
        <div v-if="loading.assignments" class="d-flex align-center justify-center">
            <v-progress-circular size="20" indeterminate color="primary" width="2"></v-progress-circular>
        </div>
        <v-card v-else>
            <v-card-title class="headline flex-column pa-2">
                <div class="d-flex fill-width">
                    <div class="flex-grow-1">{{ title }}</div>
                    <v-btn icon @click="dialog = false" class="float-right"><v-icon>mdi-close</v-icon></v-btn>
                </div>
            </v-card-title>

            <v-card-text>
                <div v-if="!referee">
                    <v-alert v-if="allInvited" type="success">{{$translate('All players have been invited')}}</v-alert>
                    <v-btn v-else block color="primary" @click="beforeInviteAll" @loading="loadingInviteAll">{{$translate('Invite all players')}}</v-btn>
                    <teamsport-invite-all-dialog v-model="showInviteAllDialog" @answer="handleInviteAllAnswer" />
                </div>
                <div v-for="player in players" :key="player.id">
                    <div class="user-list-item">
                        <v-avatar size="30">
                            <img :src="'/api/memberapi/image/member/get?size=50x50&id=' + player.id" />
                        </v-avatar>
                        <div class="ml-2">
                            <v-icon color="success" v-if="player.booked">mdi-check-circle</v-icon>
                            <v-icon color="error" v-else-if="player.declined">mdi-close-circle</v-icon>
                            <v-icon color="gray" v-else>mdi-help-circle</v-icon>
                        </div>
                        <div class="d-flex flex-column ml-2">
                            <div>{{player.name}}</div>
                            <div v-if="membershipRequired && !player.membership"><v-icon small color="error">mdi-account-off</v-icon>{{$translate('Missing membership')}}</div>
                        </div>
                        <v-spacer/>

                        <v-btn v-if="loading[player.id]" icon :loading="true">
                            <v-icon color="primary">mdi-plus-circle</v-icon>
                        </v-btn>
                        <template v-else>
                            <v-btn icon @click="book(player)" v-if="!player.booked && (!membershipRequired || player.membership)" :loading="loading[player.id]">
                                <v-icon color="primary">mdi-plus-circle</v-icon>
                            </v-btn>
                            <v-btn icon @click="cancel(player)" v-if="player.booked" :loading="loading[player.id]">
                                <v-icon color="error">mdi-close</v-icon>
                            </v-btn>
                            <div v-if="!referee">
                                <v-btn icon @click="invite(player)" v-if="player.status == 'not invited'" :loading="loading[player.id]">
                                    <v-icon color="primary">mdi-email-outline</v-icon>
                                </v-btn>
                                <v-btn icon @click="invite(player)" v-if="player.invited || player.declined" :loading="loading[player.id]">
                                    <v-icon color="primary">mdi-email-sync-outline</v-icon>
                                </v-btn>
                            </div>
                        </template>
                    </div>
                </div>
            </v-card-text>
            <v-card-actions>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import TeamsportInviteAllDialog from '../../views/TeamsportInviteAllDialog.vue';
export default {
    name: 'zoezi-teamsport-invite-players-dialog',

    props: {
        value: {
            type: Boolean,
            default: false
        },
        match: {
            type: Object
        },
        teamId: {
            type: Number
        },
        group: {
            type: Object
        },
        activity: {
            type: Object
        },
        referee: {
            type: Boolean
        }
    },

    components: {
        TeamsportInviteAllDialog
    },

    computed: {
        dialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        allInvited() {
            return this.players.every(player => player.status != 'not invited');
        },
        hasMembership() {
            return this.$store.state?.settings?.membership;
        },
        membershipRequired() {
            if(this.referee) {
                const assingnment =  this.refereeAssignments.find(a => a.division.id == this.match.division_id);
                return this.hasMembership && assingnment?.competition.require_membership;
            } else {
                const registration = this.$store.state?.bookings?.teamsportbookings?.find(b => b.id == this.teamId);
                return this.hasMembership && registration?.competition?.require_membership;
            }
        },
        title() {
            return this.referee ? this.$translate('Manage formation') : this.$translate('Add / call players');
        },
        team() {
            return this.match.teams.find(team => team.id == this.teamId);
        },
        players() {
            if (!this.team || !this.formation) {
                return [];
            }

            return this.team.group.users.map(user_id => {
                const user = this.formation.find(x => x.id == user_id);
                if (!user) {
                    return null;
                }
                return {
                    name: user.name,
                    id: user_id,
                    shirt_number: this.match.shirt_numbers?.find(x => x.user == user_id)?.shirt,
                    status: user.status,
                    booked: user.status == 'booked',
                    invited: user.status == 'invited',
                    declined: user.status == 'declined',
                    membership: user.membership
                }
            }).filter(x => x).sort((a, b) => a.name.localeCompare(b.name));
        },
    },

    data: () => ({
        loading: {},
        loadingInviteAll: false,
        showInviteAllDialog: false,
        unsubscribe: [],
        refereeAssignments: [],
        formation: []
    }),

    watch: {
        players: {
            immediate: true,
            handler() {
                this.players.forEach(player => {
                    this.$set(this.loading, player.id, false);
                });
            }
        },
        '$store.state.user.showRefereeSchedule': {
            immediate: true,
            handler(val) {
                if (val) {
                    this.getAssignments();
                }
            }
        },
        group: {
            immediate: true,
            handler() {
                this.getFormation();
            }
        }
    },

    methods: {
        getFormation() {
            if (!this.group.id) {
                return;
            }
            this.loading.formation = true;
            this.$api.get('/api/teamsport/match/team/formation/status', { match: this.match.id, group: this.group.id }).then(formation => {
                this.formation = formation;
                this.loading.formation = false;
            });
        },
        getAssignments() {
            this.loading.assignments = true;
            this.$api.get('/api/memberapi/teamsport/referee/assignments').then((assignments) => {
                this.refereeAssignments = assignments;
                this.loading.assignments = false;
            });
        },
        possiblyShowError(response) {
            if (!response.ok) {
                response.json().then(data => {
                    this.$store.commit('showErrorDialog', {title: 'Fel', text: 'Något gick fel. ' + this.$translate(data.message)});
                });
            }
        },
        book(player) {
            this.loading[player.id] = true;
            this.$api.post('/api/memberapi/teamsport/match/bookings/set', {id: player.id, match_id: this.match.id, group_id: this.group.id }).then(response => {
                this.loading[player.id] = false;
                this.possiblyShowError(response);
            });
        },
        cancel(player) {
            this.loading[player.id] = true;
            this.$api.post('/api/memberapi/teamsport/match/bookings/remove', {id: player.id, match_id: this.match.id, group_id: this.group.id }).then(response => {
                this.loading[player.id] = false;
                this.possiblyShowError(response);
            });
        },
        invite(recipients, options) {
            if (!Array.isArray(recipients)) {
                recipients = [recipients];
            }
            recipients = recipients.map(r => r.id);
            options = options || {};
            options.match_id = this.match.id;
            options.group_id = this.group.id;
            options.team_activity = true;

            const data = {
                sendtype: 'mail',
                workout_id: this.activity.id,
                recipients,
                option: options
            };

            recipients.forEach(id => {
                this.loading[id] = true;
            });
            return this.$api.post('/api/workoutBooking/invites/add', data).then(response => {
                recipients.forEach(id => {
                    this.loading[id] = false;
                });
                this.possiblyShowError(response);
            });
        },
        inviteAll(options) {
            let recipients = this.players.filter(player => player.status == 'not invited');
            this.loadingInviteAll = true;
            this.invite(recipients, options).then(() => {
                this.loadingInviteAll = false;
            })
        },
        beforeInviteAll() {
            if (this.group.autoinvite_dont_ask_again) {
                this.inviteAll();
                return;
            }
            this.showInviteAllDialog = true;
        },
        handleInviteAllAnswer(answer) {
            this.showInviteAllDialog = false;
            this.inviteAll({automatic_invite: answer});
        }
    },

    mounted() {
        this.unsubscribe.push(this.$api.subscribe({type: 'teamsportmatch', callback: (_, event) => {
            if(event.data?.referees_id?.includes(this.$store.state.user.id)) {
                this.getAssignments();
            }
        }}))

        this.unsubscribe.push(this.$api.subscribe({type: 'workoutbooking', callback: (_, event) => {
            const teamActivityIds = this.match.team_activities.map(i => i?.id ?? i);
            const matchingActivityId = event.data.find(item => teamActivityIds.includes(item.workout))

            if (matchingActivityId) {
                this.getFormation();
            }
        }}));

        this.unsubscribe.push(this.$api.subscribe({type: 'workoutbookinginvite', callback: (_, event) => {
            const teamActivityIds = this.match.team_activities.map(i => i?.id ?? i);
            const matchingActivityId = event.data.find(item => teamActivityIds.includes(item.workout_id))

            if (matchingActivityId) {
                this.getFormation();
            }
        }}));
    },

    destroyed() {
        this.unsubscribe.forEach(x => x());
    }
};
</script>
